import { Message } from "@cnx/common";
import DocumentPreview from "components/ImageUploader/DocumentPreview";
import { useState } from "react";
import { ImageMessageView } from "./ImageMessageView";

type MessageAttachmentProps = {
  item: Message;
};
const THUMB = 140;
const PORTRAIT = 175;
const GAP = 4;

const isPdf = (blob: Blob) => blob.type === "application/pdf";

const MessageAttachment = ({ item }: MessageAttachmentProps) => {
  const [showModal, setShowModal] = useState(false);
  const [initialIndex, setInitialIndex] = useState(0);

  if (!item.attachments?.length) return null;

  const count = item.attachments.length;
  const docs = item.attachments.map(({ attachmentBlob, id }) => ({
    id,
    blob: attachmentBlob ?? new Blob(),
  }));

  const openAt = (index: number) => {
    setInitialIndex(index);
    setShowModal(true);
  };

  const modal = (
    <ImageMessageView
      message={item}
      showModal={showModal}
      documents={docs}
      setShowModal={setShowModal}
      initialIndex={initialIndex}
    />
  );

  if (count === 1) {
    return (
      <div>
        {modal}
        <div className="py-[7px] px-1">
          <DocumentPreview
            width={280}
            height={360}
            removable={false}
            onClick={() => openAt(0)}
            document={docs[0].blob}
            borderColor="transparent"
            isPdf={isPdf(docs[0].blob)}
          />
        </div>
      </div>
    );
  }

  if (count === 2) {
    return (
      <div>
        {modal}
        <div className="flex w-fit py-[7px]" style={{ gap: GAP }}>
          {docs.map((doc, i) => (
            <DocumentPreview
              key={doc.id ?? i}
              width={THUMB}
              height={PORTRAIT}
              removable={false}
              onClick={() => openAt(i)}
              document={doc.blob}
              borderColor="transparent"
              isPdf={isPdf(doc.blob)}
            />
          ))}
        </div>
      </div>
    );
  }

  if (count === 3) {
    const bigH = THUMB * 2 + GAP;
    return (
      <div>
        {modal}
        <div className="flex w-fit py-[7px]" style={{ gap: GAP }}>
          <DocumentPreview
            width={THUMB}
            height={bigH}
            removable={false}
            onClick={() => openAt(0)}
            document={docs[0].blob}
            borderColor="transparent"
            isPdf={isPdf(docs[0].blob)}
          />
          <div className="flex flex-col" style={{ gap: GAP }}>
            {docs.slice(1, 3).map((doc, index) => (
              <DocumentPreview
                key={doc.id}
                width={THUMB}
                height={THUMB}
                removable={false}
                onClick={() => openAt(index + 1)}
                document={doc.blob}
                borderColor="transparent"
                isPdf={isPdf(doc.blob)}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const sliced = docs.slice(0, 4);
  const remaining = count > 4 ? count - 4 : 0;

  return (
    <div>
      {modal}
      <div className="grid grid-cols-2 w-fit py-[7px]" style={{ gap: GAP }}>
        {sliced.map((doc, index) => (
          <div key={doc.id} className="relative">
            <DocumentPreview
              width={THUMB}
              height={THUMB}
              removable={false}
              onClick={() => openAt(index)}
              document={doc.blob}
              borderColor="transparent"
              isPdf={isPdf(doc.blob)}
            />
            {index === 3 && remaining > 0 && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-md pointer-events-none">
                <span className="text-white text-xl font-bold">
                  +{remaining + 1}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MessageAttachment;
